import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Users, ArrowUpRight } from "lucide-react";
import { EDITORIAL_BOARDS } from "@/data/editorialBoards";

/**
 * Curated 6-member spotlight carousel built from the scraped editorial
 * boards. Picks the Editor-in-Chief + a few specialty leads from both
 * journals so the publisher's editorial credibility is front-and-centre.
 */
const SPOTLIGHT = [
  {
    journalId: "ijmrcr",
    role: "Editor-in-Chief",
    name: "Ivan Inkov",
    affiliation:
      "Thoracic Surgery Fellow, Military Medical Academy of Sofia · CAS Lung Cancer (Zurich) · CCB Breast Cancer (Ulm)",
    country: "Bulgaria",
    orcid: "0000-0001-6084-1707",
    accent: "var(--sci-teal)",
  },
  {
    journalId: "ijsm",
    role: "Associate Editor",
    name: "Adel Hamed Elbaih",
    affiliation:
      "Assistant Professor of Emergency Medicine, Suez Canal University, Ismailia",
    country: "Egypt",
    orcid: "0000-0002-3347-5163",
    accent: "var(--sci-gold)",
  },
  {
    journalId: "ijsm",
    role: "Associate Editor",
    name: "Arda Asik",
    affiliation:
      "Dept of General Surgical Oncology · Breast Surgery, Pittsburgh",
    country: "USA",
    orcid: "0000-0001-9493-4055",
    accent: "var(--sci-teal)",
  },
  {
    journalId: "ijsm",
    role: "Editorial Board · Cardiovascular & Thoracic Surgery",
    name: "Tsvetomir Loukanov",
    affiliation:
      "Head, Clinic of Pediatric Cardiac Surgery, Universitätsklinikum Heidelberg",
    country: "Germany",
    accent: "var(--sci-navy)",
  },
  {
    journalId: "ijmrcr",
    role: "Editorial Board · Gynecologic Oncology",
    name: "Gennaro Cormio",
    affiliation:
      "Gynecologic Oncology Unit, University of Bari Aldo Moro",
    country: "Italy",
    accent: "var(--sci-teal)",
  },
  {
    journalId: "ijsm",
    role: "Editorial Board · Coloproctology",
    name: "Aikaterini Mastoraki",
    affiliation:
      "First Dept of Surgery, National and Kapodistrian University of Athens",
    country: "Greece",
    accent: "var(--sci-gold)",
  },
];

function initials(name) {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("");
}

export default function EditorialBoardSpotlight() {
  const [idx, setIdx] = useState(0);
  const total = SPOTLIGHT.length;

  // Auto-rotate every 6s, paused on hover
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 6000);
    return () => clearInterval(t);
  }, [total]);

  const current = SPOTLIGHT[idx];
  const journal = EDITORIAL_BOARDS[current.journalId];

  return (
    <section
      data-testid="board-spotlight-section"
      className="relative py-24 md:py-32 bg-[var(--sci-bg)] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start mb-12">
          <div className="lg:col-span-6">
            <div className="overline text-[var(--sci-teal)] mb-5 flex items-center gap-2">
              <Users size={14} /> · Editorial Board Spotlight
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02]">
              The people behind the <em className="not-italic text-[var(--sci-teal)]">imprint</em>.
            </h2>
          </div>
          <p className="lg:col-span-6 text-[var(--sci-text-muted)] leading-relaxed text-base md:text-lg lg:pt-4">
            Sci Ink Press is curated by an international team of clinicians and
            researchers — selected for depth in surgery, internal medicine,
            oncology, paediatric cardiac surgery, and emergency medicine.
          </p>
        </div>

        <div
          data-testid="board-spotlight-card"
          className="relative bg-white border border-[var(--sci-border)] overflow-hidden"
          onMouseEnter={() => {}}
        >
          <div className="grid md:grid-cols-12 min-h-[380px]">
            {/* Avatar / monogram panel */}
            <div
              className="md:col-span-4 relative flex items-center justify-center p-10 md:p-12 bg-[var(--sci-navy)] text-white overflow-hidden"
              style={{
                background:
                  "linear-gradient(160deg, var(--sci-navy) 0%, #0f1e3a 100%)",
              }}
            >
              <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
              <div
                className="absolute -right-20 -top-20 w-72 h-72 rounded-full pointer-events-none"
                style={{
                  background:
                    "radial-gradient(closest-side, " +
                    current.accent +
                    "55, transparent)",
                }}
              />
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.name}
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4 }}
                  className="relative flex flex-col items-center text-center"
                >
                  <div
                    className="w-32 h-32 md:w-36 md:h-36 border-2 flex items-center justify-center font-serif-display text-5xl md:text-6xl tracking-tight"
                    style={{ borderColor: current.accent, color: "white" }}
                  >
                    {initials(current.name)}
                  </div>
                  <div className="mt-6 overline text-white/70">
                    {current.country}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Detail panel */}
            <div className="md:col-span-8 relative p-8 md:p-12 lg:p-14 flex flex-col">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.name + "-detail"}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.45 }}
                  className="flex-1"
                  data-testid="board-spotlight-detail"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span
                      className="inline-block w-2 h-2"
                      style={{ background: current.accent }}
                    />
                    <span className="overline text-[var(--sci-text-muted)]">
                      {current.role} · {journal?.journalShort}
                    </span>
                  </div>
                  <h3
                    className="font-serif-display text-3xl md:text-5xl text-[var(--sci-navy)] tracking-tight leading-[1.02]"
                    data-testid="board-spotlight-name"
                  >
                    {current.name}
                  </h3>
                  <p className="mt-5 text-[var(--sci-text-muted)] text-base md:text-lg leading-relaxed max-w-2xl">
                    {current.affiliation}
                  </p>

                  <div className="mt-7 flex flex-wrap items-center gap-4 text-xs">
                    {current.orcid && (
                      <a
                        href={`https://orcid.org/${current.orcid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-mono-sci tracking-wider text-[var(--sci-navy)] border border-[var(--sci-border)] px-3 py-1.5 hover:border-[var(--sci-teal)] hover:text-[var(--sci-teal)] transition-colors"
                      >
                        ORCID · {current.orcid}
                      </a>
                    )}
                    <Link
                      to={`/editorial-board/${current.journalId}`}
                      data-testid="board-spotlight-cta"
                      className="inline-flex items-center gap-2 text-sm font-medium text-[var(--sci-navy)] hover:text-[var(--sci-teal)] border-b border-[var(--sci-navy)] hover:border-[var(--sci-teal)] pb-0.5 transition-colors"
                    >
                      Full {journal?.journalShort} board
                      <ArrowUpRight size={14} />
                    </Link>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Carousel controls */}
              <div className="mt-10 flex items-center justify-between border-t border-[var(--sci-border)] pt-5">
                <div className="font-mono-sci text-xs tracking-wider text-[var(--sci-text-muted)]">
                  {String(idx + 1).padStart(2, "0")} /{" "}
                  {String(total).padStart(2, "0")}
                </div>
                <div className="flex items-center gap-2">
                  {SPOTLIGHT.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setIdx(i)}
                      data-testid={`spotlight-dot-${i}`}
                      aria-label={`Show editor ${i + 1}`}
                      className={`w-6 h-px transition-colors ${
                        i === idx ? "bg-[var(--sci-teal)]" : "bg-[var(--sci-border)]"
                      }`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setIdx((i) => (i - 1 + total) % total)}
                    data-testid="spotlight-prev"
                    aria-label="Previous editor"
                    className="w-9 h-9 border border-[var(--sci-border)] hover:border-[var(--sci-teal)] hover:text-[var(--sci-teal)] inline-flex items-center justify-center text-[var(--sci-text-muted)] transition-colors"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setIdx((i) => (i + 1) % total)}
                    data-testid="spotlight-next"
                    aria-label="Next editor"
                    className="w-9 h-9 border border-[var(--sci-border)] hover:border-[var(--sci-teal)] hover:text-[var(--sci-teal)] inline-flex items-center justify-center text-[var(--sci-text-muted)] transition-colors"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
